import { useContext } from "react";

import {
  PublicationVersionConflictResponseFromJSON,
  PublicationVersionRead,
  PublicationVersionReadFromJSON,
  getRanaApiV1,
} from "@nens/rana/api-v1";
import { TenantContext, useMutationWithErrorHandler } from "@nens/ranalib";
import { ApiError } from "@nens/react-query-utils";

import { PublicationVersionFunctionsContext } from "../components/store-wrapper";
import { getIsChanged } from "../store/selectors";
import { usePublicationStore } from "../store/store";
import { getNewVersionCreateFromPublication } from "../store/transformers";
import { StorePublication } from "../types/publication";

// In case of a conflict, we want to store these things to show to the user and help them decide what to do.
export interface ConflictDetails {
  conflictVersion: number;
  conflictCreatedAt: Date;
  conflictCreatedByName: string;
}

export function useSavePublicationVersion(
  conflictDetails: ConflictDetails | null,
  setConflictDetails: (details: ConflictDetails | null) => void
) {
  const publication: StorePublication = usePublicationStore((store) => store);
  const { tenant } = useContext(TenantContext);
  const isChanged = usePublicationStore(getIsChanged);
  const { setPublicationVersion } = useContext(PublicationVersionFunctionsContext);

  return useMutationWithErrorHandler<PublicationVersionRead | null, ApiError, boolean>(
    async (forceConflict = false) => {
      if (!isChanged) {
        console.warn("Trying to save publication without changes");
        return null;
      }
      const publicationVersionCreate = getNewVersionCreateFromPublication(publication);
      // If there was a conflict previously (conflictDetails is not null) and the user chose to
      // force update, we set the version to conflictVersion + 1 to try to save anyway (conflictVersion
      // is the version that caused the conflict)..
      // This will only succeed if there was no new conflicting version created in the meantime,
      //  otherwise a new conflict will arise and the dialog will remain open.
      if (conflictDetails && forceConflict) {
        publicationVersionCreate.version = conflictDetails.conflictVersion + 1;
      }

      const ranaApi = getRanaApiV1();

      const urlPath = `/tenants/${tenant}/publications/${publication.id}/versions`;

      try {
        const newVersion: PublicationVersionRead = PublicationVersionReadFromJSON(
          await ranaApi.BaseApi.fetchJson(urlPath, {
            method: "POST",
            body: JSON.stringify(publicationVersionCreate),
          })
        );

        await setPublicationVersion(newVersion);

        return newVersion;
      } catch (e) {
        // A conflict may arise if someone else has created a new version of the publication since the last time we fetched it.
        // In that case, a 409 HTTP response with details about the conflict will be returned.
        // BaseApi.fetch throws an ApiError for non-OK responses, so we catch 409 here to handle the conflict.
        if (e instanceof ApiError && e.getStatusCode() === 409) {
          const errorBody = PublicationVersionConflictResponseFromJSON(e.getBody());

          // Set the fields we are interested in, so that the conflict modal can open
          setConflictDetails({
            conflictVersion: errorBody.detail.version,
            conflictCreatedAt: errorBody.detail.createdAt ?? new Date(),
            conflictCreatedByName: errorBody.detail.createdByName ?? "Unknown user",
          });
          /* We return null here to indicate that the save was not successful,
             but we don't want to throw an error because that shows the error toast. */
          return null;
        }
        throw e;
      }
    }
  );
}
